import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PublicNav from "../components/PublicNav";
import {
  ArrowRight,
  Sparkles,
  Target,
  Heart,
  Lightbulb,
  Users,
  Globe,
  Zap,
} from "lucide-react";

export default function AboutPage() {
  const values = [
    {
      icon: Target,
      title: "Mission-Driven",
      description: "We're on a mission to democratize AI for African businesses, making powerful automation accessible to everyone."
    },
    {
      icon: Heart,
      title: "Customer First",
      description: "Every feature we build starts with understanding our customers' needs and solving their real problems."
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "We continuously push boundaries to bring cutting-edge AI technology to the WhatsApp platform."
    }
  ];

  const stats = [
    { value: "1000+", label: "Active Agents" },
    { value: "500K+", label: "Messages Processed" },
    { value: "98%", label: "Uptime" },
    { value: "24/7", label: "Support" }
  ];

  return (
    <>
      <SEO
        title="About"
        description="Learn about Botibonga's mission to empower African businesses with AI-powered WhatsApp chatbots. Our story, values, and team."
        path="/about"
      />

      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)' }}>
        <PublicNav />

        {/* Hero */}
        <section className="px-6 py-16 lg:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6"
                 style={{
                   background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                   border: '1px solid rgba(99, 102, 241, 0.3)'
                 }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: '#6366f1' }} />
              <span style={{ color: '#6366f1' }}>Our Story</span>
            </div>

            <h1 className="text-3xl lg:text-5xl font-bold mb-6" style={{ color: '#1e293b' }}>
              Empowering businesses with{" "}
              <span style={{
                background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>intelligent automation</span>
            </h1>

            <p className="text-lg" style={{ color: '#64748b' }}>
              Botibonga was founded with a simple belief: every business, regardless of size,
              deserves access to powerful AI tools that can transform how they connect with customers.
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="px-6 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="p-8 rounded-2xl"
                 style={{
                   background: '#ffffff',
                   border: '1px solid #e2e8f0',
                   boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                 }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                     style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-semibold" style={{ color: '#1e293b' }}>Our Mission</h2>
              </div>
              <p className="text-lg leading-relaxed" style={{ color: '#64748b' }}>
                We believe that AI should be accessible to everyone. Our mission is to empower
                African businesses with intelligent WhatsApp automation that helps them serve
                customers better, capture more leads, and grow faster - without needing technical expertise.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="px-6 py-16" style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center p-4 rounded-xl"
                     style={{
                       background: '#ffffff',
                       border: '1px solid #e2e8f0'
                     }}>
                  <p className="text-2xl lg:text-3xl font-bold"
                     style={{
                       background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                       WebkitBackgroundClip: 'text',
                       WebkitTextFillColor: 'transparent',
                       backgroundClip: 'text'
                     }}>
                    {stat.value}
                  </p>
                  <p className="text-sm" style={{ color: '#64748b' }}>{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="px-6 py-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold text-center mb-12" style={{ color: '#1e293b' }}>
              Our Values
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {values.map((value, index) => (
                <div key={index} className="p-6 rounded-xl text-center"
                     style={{
                       background: '#ffffff',
                       border: '1px solid #e2e8f0',
                       boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                     }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                       style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)' }}>
                    <value.icon className="w-6 h-6" style={{ color: '#6366f1' }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#1e293b' }}>{value.title}</h3>
                  <p className="text-sm" style={{ color: '#64748b' }}>{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why WhatsApp */}
        <section className="px-6 py-16" style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl lg:text-3xl font-bold mb-6" style={{ color: '#1e293b' }}>
              Why WhatsApp?
            </h2>
            <p className="text-lg mb-8" style={{ color: '#64748b' }}>
              WhatsApp is the most used messaging app in Africa, with over 100 million users.
              By building on WhatsApp, we help businesses meet their customers where they already are,
              making it easier to provide support, capture leads, and build relationships.
            </p>
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg"
                   style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
                <Users className="w-5 h-5" style={{ color: '#6366f1' }} />
                <span className="text-sm font-medium" style={{ color: '#1e293b' }}>100M+ African Users</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg"
                   style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
                <Zap className="w-5 h-5" style={{ color: '#8b5cf6' }} />
                <span className="text-sm font-medium" style={{ color: '#1e293b' }}>Instant Delivery</span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4" style={{ color: '#1e293b' }}>
              Join our journey
            </h2>
            <p className="mb-8" style={{ color: '#64748b' }}>
              Be part of the AI revolution in African business communication.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-medium transition-all hover:scale-105"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
                  }}>
              Get Started Today
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-8" style={{ borderTop: '1px solid #e2e8f0' }}>
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm" style={{ color: '#94a3b8' }}>
              © {new Date().getFullYear()} Botibonga. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-sm transition-colors" style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                    onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
                Terms
              </Link>
              <Link to="/privacy" className="text-sm transition-colors" style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                    onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
                Privacy
              </Link>
              <Link to="/contact" className="text-sm transition-colors" style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                    onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
                Contact
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
